/**
 * Slash-menu support plugin.
 *
 *   - Watches the doc state and, whenever the user is typing a `/foo` query at
 *     the start of an empty paragraph (or the only content of one), broadcasts
 *     `{ active: true, query, rect }` to a Vue-side handler.
 *   - Captures Arrow/Enter/Escape while active so the menu can react.
 *
 * The actual block insertions live in the Vue component and call back into
 * the EditorView via a `runCommand` ref.
 */
import { Plugin, PluginKey } from 'prosemirror-state';
import type { EditorView } from 'prosemirror-view';
import type { EditorState, Transaction } from 'prosemirror-state';

export const slashMenuPluginKey = new PluginKey('pmSlashMenu');

export interface SlashState {
  active: boolean
  query: string
  /** position right before the `/` so we can replace `[from, to]` cleanly */
  from: number
  to: number
  rect: DOMRect | null
}

export interface SlashHandlers {
  onUpdate: (state: SlashState) => void
  onKey: (key: string) => boolean
}

const EMPTY_STATE: SlashState = {
  active: false, query: '', from: 0, to: 0, rect: null,
};

function detectSlash(view: EditorView): SlashState {
  const { state } = view;
  const { $from, empty } = state.selection;
  if (!empty) return EMPTY_STATE;
  // Must be inside a paragraph.
  if ($from.parent.type.name !== 'paragraph') return EMPTY_STATE;
  // Walk backwards in the parent's textContent to find the slash.
  const textBefore = $from.parent.textBetween(0, $from.parentOffset, '\0', '\0');
  // Find last '/'; require it to be at column 0 OR preceded by whitespace.
  const m = /(^|\s)\/([^\s/]*)$/.exec(textBefore);
  if (!m) return EMPTY_STATE;
  const slashOffset = m.index + (m[1]?.length ?? 0);
  // Only fire when the slash sits at the *start* of the paragraph, per spec.
  if (slashOffset !== 0) return EMPTY_STATE;
  const query = m[2] ?? '';
  const from = $from.start() + slashOffset;
  const to = $from.start() + $from.parentOffset;
  const startCoords = view.coordsAtPos(from);
  const endCoords = view.coordsAtPos(to);
  const rect = new DOMRect(
    startCoords.left,
    startCoords.top,
    endCoords.right - startCoords.left,
    startCoords.bottom - startCoords.top,
  );
  return { active: true, query, from, to, rect };
}

export function buildSlashMenuPlugin(handlers: SlashHandlers) {
  let last: SlashState = EMPTY_STATE;
  return new Plugin({
    key: slashMenuPluginKey,
    view: (view) => {
      const handle = () => {
        const next = detectSlash(view);
        const changed = next.active !== last.active
          || next.query !== last.query
          || next.from !== last.from
          || next.to !== last.to;
        if (changed) {
          last = next;
          handlers.onUpdate(next);
        }
      };
      handle();
      return {
        update() { handle(); },
        destroy() {
          last = EMPTY_STATE;
          handlers.onUpdate(EMPTY_STATE);
        },
      };
    },
    props: {
      handleKeyDown(_view, event) {
        if (!last.active) return false;
        if (
          event.key === 'ArrowUp'
          || event.key === 'ArrowDown'
          || event.key === 'Enter'
          || event.key === 'Escape'
        ) {
          if (handlers.onKey(event.key)) {
            event.preventDefault();
            return true;
          }
        }
        return false;
      },
    },
  });
}

/** Helper: replace the `/foo` query with the result of `build(tr)`. */
export function replaceSlashRange(
  state: EditorState,
  from: number,
  to: number,
  apply: (tr: Transaction) => Transaction,
): Transaction {
  let tr = state.tr.delete(from, to);
  tr = apply(tr);
  return tr;
}
